import useErrorHandling, { ErrorTypes } from "../../../errors/useErrorHandling";
import usePreferences from "./usePreferences";
import { SERVICENOW } from "../../../constants/serviceNames";
import useServiceNowUser from "./useServiceNowUser";
import integrationsClient from "../clients/integrationsClient";
import serviceNowMapper from "../mappers/serviceNowMapper";
import useSWRInfinite from "swr/infinite";
import { useMemo } from "react";
import { PROCURE_TO_PAY_INCIDENT_ROWS_PER_PAGE } from "../../../constants/generalConstants";
import { countAPIItems, reduceAPIItems } from "../../../utils/apiItemUtils";

const useServiceNowProcurementCases = (viewClosed) => {
    const { servicesInMaintenanceMode, preferencesAreLoading } =
        usePreferences();
    const {
        serviceNowUser,
        serviceNowUserIsLoading,
        errorMessage: serviceNowUserErrorMessage,
    } = useServiceNowUser();
    const { throwAsyncError } = useErrorHandling();

    const getCacheKey = (pageCount) => {
        if (
            preferencesAreLoading ||
            serviceNowUserIsLoading ||
            !serviceNowUser
        ) {
            return null;
        }
        return ["procurement-cases", pageCount, viewClosed];
    };

    const {
        data: procurementCasesData,
        size: loadedPageCount,
        setSize: setPagesToLoad,
        isLoading: procurementCasesAreLoading,
        error: procurementCasesError,
    } = useSWRInfinite(
        getCacheKey,
        async (args) => {
            try {
                const params = {
                    limit: PROCURE_TO_PAY_INCIDENT_ROWS_PER_PAGE,
                    offset: args[1] * PROCURE_TO_PAY_INCIDENT_ROWS_PER_PAGE,
                    show_closed: args[2],
                };
                if (servicesInMaintenanceMode && serviceNowUser) {
                    if (
                        !servicesInMaintenanceMode.some(
                            (service) => service === SERVICENOW
                        )
                    ) {
                        const pCasesData =
                            await integrationsClient.getProcurementCases(
                                params,
                                {
                                    "sn-user-data-token": serviceNowUser,
                                }
                            );
                        return serviceNowMapper.mapAPIProcurementCasesToCaseRequests(
                            pCasesData
                        );
                    }
                }
            } catch (error) {
                throwAsyncError(
                    ErrorTypes.ServiceNowIncidentsError,
                    "Unable to fetch incidents from Procure To Pay",
                    error
                );
                throw new Error(
                    "Unable to fetch incidents from Procure To Pay"
                );
            }
        },
        { fallbackData: [], parallel: true, revalidateFirstPage: false }
    );

    const procurementCaseList = useMemo(() => {
        return reduceAPIItems(procurementCasesData, []);
    }, [procurementCasesData]);

    const procurementCaseCount = useMemo(() => {
        return countAPIItems(procurementCasesData);
    }, [procurementCasesData]);

    return {
        procurementCasesAreLoading,
        procurementCaseCount,
        procurementCaseList,
        loadedPageCount,
        setPagesToLoad,
        errorMessage:
            serviceNowUserErrorMessage || procurementCasesError?.message,
    };
};

export default useServiceNowProcurementCases;
